import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const MENSAJES_RAPIDOS = [
  "Feliz Cumpleanos! Bendiciones!",
  "Que Dios te bendiga en tu dia!",
  "Felicidades! Un abrazo grande!",
  "Muchas bendiciones en tu dia especial!",
  "Feliz dia! Que sea un ano lleno de bendiciones!"
];

const CumpleanosWidgetCuadrado = () => {
  const { user, API, getImageUrl } = useContext(AuthContext);
  const [cumpleaneros, setCumpleaneros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [showFelicitacion, setShowFelicitacion] = useState(false);
  const [mensajePersonalizado, setMensajePersonalizado] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [felicitacionesEnviadas, setFelicitacionesEnviadas] = useState({});
  const [felicitacionesRecibidas, setFelicitacionesRecibidas] = useState([]);
  const [showRecibidas, setShowRecibidas] = useState(false);
  const [esMiCumple, setEsMiCumple] = useState(false);
  const audioRef = useRef(null);

  const birthdaySoundUrl = 'https://media.vole.wtf/happy-birthday/orchestral.mp3';

  useEffect(() => {
    if (user) {
      loadCumpleaneros();
    }
  }, [user]);

  useEffect(() => {
    if (cumpleaneros.length > 0 && !hasAutoPlayed && !loading) {
      const timer = setTimeout(() => {
        playBirthdaySound();
        setHasAutoPlayed(true);
        sessionStorage.setItem('birthday_sound_played', 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [cumpleaneros, hasAutoPlayed, loading]);

  useEffect(() => {
    if (sessionStorage.getItem('birthday_sound_played') === 'true') {
      setHasAutoPlayed(true);
    }
  }, []);

  const loadCumpleaneros = async () => {
    try {
      const response = await axios.get(`${API}/users/cumpleanos-hoy`);
      const lista = response.data.cumpleaneros || [];
      setCumpleaneros(lista);

      // Verificar si yo soy el cumpleanero
      const soyYo = lista.some(c => c.id === user?.id);
      setEsMiCumple(soyYo);

      // Si es mi cumple, cargar felicitaciones recibidas
      if (soyYo) {
        loadFelicitacionesRecibidas();
      }

      // Verificar cuales ya felicite
      for (const c of lista) {
        if (c.id !== user?.id) {
          try {
            const check = await axios.get(`${API}/felicitaciones/enviadas/${c.id}`);
            if (check.data.ya_enviada) {
              setFelicitacionesEnviadas(prev => ({ ...prev, [c.id]: true }));
            }
          } catch {}
        }
      }
    } catch (error) {
      console.error('Error al cargar cumpleanos:', error);
      setCumpleaneros([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFelicitacionesRecibidas = async () => {
    try {
      const response = await axios.get(`${API}/felicitaciones/recibidas`);
      setFelicitacionesRecibidas(response.data.felicitaciones || []);
    } catch {}
  };

  const handleEnviarFelicitacion = async (destinatarioId, mensaje) => {
    if (!mensaje.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }
    setEnviando(true);
    try {
      await axios.post(`${API}/felicitaciones/enviar`, {
        destinatario_id: destinatarioId,
        mensaje: mensaje.trim()
      });
      setFelicitacionesEnviadas(prev => ({ ...prev, [destinatarioId]: true }));
      setShowFelicitacion(false);
      setMensajePersonalizado('');
      toast.success('Felicitacion enviada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    } finally {
      setEnviando(false);
    }
  };

  const playBirthdaySound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0.4;
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const stopSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    if (isPlaying) stopSound();
    else playBirthdaySound();
  };

  if (loading || cumpleaneros.length === 0) return null;

  const cumpleaneroPrincipal = cumpleaneros[0];
  const otrosCumpleaneros = cumpleaneros.slice(1);
  const todosParaFelicitar = cumpleaneros.filter(c => c.id !== user?.id);

  return (
    <div className="relative bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600 rounded-2xl p-4 transform transition-all duration-300 hover:shadow-2xl"
      style={{ minHeight: '220px' }}
    >
      <audio ref={audioRef} src={birthdaySoundUrl} onEnded={() => setIsPlaying(false)} preload="auto" />

      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-2 left-4 text-yellow-300 text-xl animate-bounce" style={{ animationDelay: '0s' }}>🎊</div>
        <div className="absolute top-3 right-5 text-yellow-300 text-lg animate-bounce" style={{ animationDelay: '0.2s' }}>🎉</div>
        <div className="absolute bottom-12 left-6 text-yellow-300 text-base animate-bounce" style={{ animationDelay: '0.4s' }}>✨</div>
        <div className="absolute bottom-8 right-3 text-yellow-300 text-xl animate-bounce" style={{ animationDelay: '0.3s' }}>🎈</div>
      </div>

      <div className="relative z-10 flex flex-col items-center text-white">
        {/* Titulo */}
        <p className="text-base font-black drop-shadow-md mb-2">🎂 Feliz Cumpleanos! 🎂</p>

        {/* Foto principal */}
        <div className="relative mb-2">
          {cumpleaneroPrincipal.foto_perfil ? (
            <img
              src={getImageUrl ? getImageUrl(cumpleaneroPrincipal.foto_perfil) : cumpleaneroPrincipal.foto_perfil}
              alt={cumpleaneroPrincipal.nombre_completo}
              className="w-16 h-16 rounded-full object-cover border-3 border-white shadow-xl"
              onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(cumpleaneroPrincipal.nombre_completo || 'U')}&background=random&color=fff&size=128`; }}
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center border-3 border-white shadow-xl">
              <span className="text-2xl font-bold">{cumpleaneroPrincipal.nombre_completo?.charAt(0)?.toUpperCase() || '🎂'}</span>
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 bg-yellow-400 rounded-full w-6 h-6 flex items-center justify-center shadow-md border-2 border-white">
            <span className="text-xs">🎈</span>
          </div>
        </div>

        {/* Nombre */}
        <p className="text-sm font-bold drop-shadow-md line-clamp-1 mb-1">{cumpleaneroPrincipal.nombre_completo}</p>
        {cumpleaneroPrincipal.edad && (
          <p className="text-xs opacity-90 font-semibold">Cumple {cumpleaneroPrincipal.edad} anos! 🎉</p>
        )}

        {otrosCumpleaneros.length > 0 && (
          <p className="text-xs opacity-80 mt-1">Y tambien: {otrosCumpleaneros.map(c => c.nombre_completo?.split(' ')[0]).join(', ')}</p>
        )}

        {/* Botones de accion */}
        <div className="flex gap-2 mt-3 w-full justify-center flex-wrap">
          {/* Boton melodia */}
          <button
            onClick={toggleSound}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 flex items-center gap-1.5 ${
              isPlaying ? 'bg-white text-purple-600 shadow-lg scale-105' : 'bg-white/25 text-white hover:bg-white/35'
            }`}
            data-testid="birthday-sound-btn"
          >
            {isPlaying ? (<><span className="animate-pulse">🔊</span><span>Sonando...</span></>) : (<><span>🎵</span><span>Melodia</span></>)}
          </button>

          {/* Boton enviar felicitacion (solo si NO soy yo el cumpleanero) */}
          {todosParaFelicitar.length > 0 && !showFelicitacion && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowFelicitacion(true); setShowRecibidas(false); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 flex items-center gap-1.5 ${
                Object.keys(felicitacionesEnviadas).length === todosParaFelicitar.length
                  ? 'bg-green-400/30 text-white'
                  : 'bg-yellow-400/80 text-purple-900 hover:bg-yellow-400 shadow-lg'
              }`}
              data-testid="send-greeting-btn"
            >
              {Object.keys(felicitacionesEnviadas).length === todosParaFelicitar.length
                ? (<><span>✅</span><span>Enviada</span></>)
                : (<><span>💌</span><span>Felicitar</span></>)
              }
            </button>
          )}

          {/* Boton ver felicitaciones recibidas (si es mi cumple) */}
          {esMiCumple && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowRecibidas(!showRecibidas); setShowFelicitacion(false); loadFelicitacionesRecibidas(); }}
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-pink-400/80 text-white hover:bg-pink-400 transition-all flex items-center gap-1.5 shadow-lg"
              data-testid="view-greetings-btn"
            >
              <span>💝</span>
              <span>Mis Felicitaciones {felicitacionesRecibidas.length > 0 ? `(${felicitacionesRecibidas.length})` : ''}</span>
            </button>
          )}
        </div>

        {/* Panel de enviar felicitacion - overlay */}
        {showFelicitacion && (
          <div className="absolute left-0 right-0 top-0 z-50 bg-white rounded-2xl p-4 shadow-2xl border-2 border-purple-300" style={{ minWidth: '280px' }} onClick={(e) => e.stopPropagation()}>
            {todosParaFelicitar.map(cumple => {
              const yaEnviada = felicitacionesEnviadas[cumple.id];
              if (yaEnviada) {
                return (
                  <div key={cumple.id} className="text-center py-2">
                    <div className="text-green-600 text-sm font-bold">✅ Ya felicitaste a {cumple.nombre_completo?.split(' ').slice(0, 2).join(' ')}</div>
                  </div>
                );
              }
              return (
                <div key={cumple.id} className="space-y-2">
                  <p className="text-purple-800 text-sm font-bold text-center">
                    💌 Envia un mensaje a {cumple.nombre_completo?.split(' ').slice(0, 2).join(' ')}:
                  </p>

                  {/* Mensajes rapidos */}
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {MENSAJES_RAPIDOS.map((msg, i) => (
                      <button
                        key={i}
                        onClick={() => handleEnviarFelicitacion(cumple.id, msg)}
                        disabled={enviando}
                        className="px-2.5 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs hover:bg-purple-200 transition-colors disabled:opacity-50 border border-purple-200"
                        data-testid={`quick-msg-${i}`}
                      >
                        {msg.length > 35 ? msg.slice(0, 35) + '...' : msg}
                      </button>
                    ))}
                  </div>

                  {/* Mensaje personalizado */}
                  <div className="flex gap-1.5 mt-1">
                    <input
                      type="text"
                      value={mensajePersonalizado}
                      onChange={(e) => setMensajePersonalizado(e.target.value)}
                      placeholder="O escribe tu mensaje..."
                      maxLength={200}
                      className="flex-1 px-3 py-2 rounded-lg border border-purple-200 text-sm text-gray-800 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-300"
                      data-testid="custom-msg-input"
                    />
                    <button
                      onClick={() => handleEnviarFelicitacion(cumple.id, mensajePersonalizado)}
                      disabled={enviando || !mensajePersonalizado.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      data-testid="send-custom-msg-btn"
                    >
                      {enviando ? '...' : 'Enviar'}
                    </button>
                  </div>
                </div>
              );
            })}

            <button
              onClick={(e) => { e.stopPropagation(); setShowFelicitacion(false); }}
              className="w-full mt-2 py-1.5 text-xs text-gray-500 hover:text-purple-600 font-semibold bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              ✕ Cerrar
            </button>
          </div>
        )}

        {/* Panel de felicitaciones recibidas - overlay */}
        {showRecibidas && esMiCumple && (
          <div className="absolute left-0 right-0 top-0 z-50 bg-white rounded-2xl p-4 shadow-2xl border-2 border-pink-300 max-h-64 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="text-purple-800 text-sm font-black text-center mb-3">💝 Felicitaciones Recibidas</p>
            {felicitacionesRecibidas.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-3">Aun no has recibido felicitaciones</p>
            ) : (
              <div className="space-y-2">
                {felicitacionesRecibidas.map((f, i) => (
                  <div key={f.id || i} className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-2.5 border border-pink-200">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-purple-200 flex items-center justify-center text-xs font-bold text-purple-700 flex-shrink-0">
                        {f.remitente_nombre?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-purple-800 truncate">{f.remitente_nombre}</p>
                        <p className="text-sm text-gray-700 leading-tight">{f.mensaje}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setShowRecibidas(false); }}
              className="w-full mt-2 py-1.5 text-xs text-gray-500 hover:text-pink-600 font-semibold bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              ✕ Cerrar
            </button>
          </div>
        )}
      </div>

      {/* Decoracion de fondo */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12" />
      <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-10 -mb-10" />
    </div>
  );
};

export default CumpleanosWidgetCuadrado;
